// src/components/legal/AceptacionLegal.js
// =========================================================================
// ACEPTAR LAS CONDICIONES DEL SERVICIO Y EL CONTRATO DE ENCARGADO
//
// Si a la clínica le falta aceptar la versión vigente de algún texto:
//   - el administrador ve esta pantalla y no puede seguir sin aceptarlos
//     (o cerrar sesión);
//   - el resto del personal sigue trabajando y ve un aviso.
// Queda registrado quién aceptó, cuándo y qué versión (base central).
// =========================================================================
import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import TextoLegal from './TextoLegal';

export default function AceptacionLegal() {
    const { esAdmin, logout } = useAuth();
    const [estado, setEstado] = useState(null);
    const [versiones, setVersiones] = useState({}); // id → versión que se ha mostrado
    const [marcados, setMarcados] = useState({});
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get('/legal').then(setEstado).catch(() => setEstado(null));
    }, []);

    const pendientes = estado?.pendientes || [];
    if (!pendientes.length) return null;

    const documentos = estado.documentos.filter(d => pendientes.includes(d.id));

    if (!esAdmin) {
        return (
            <div className="px-4 py-2 md:px-6 text-xs font-bold text-amber-800 bg-amber-50 border-b border-amber-200">
                El administrador de la clínica tiene pendiente aceptar las condiciones del servicio de Klyndu.
            </div>
        );
    }

    const todosMarcados = documentos.every(d => marcados[d.id] && versiones[d.id]);

    const aceptar = async () => {
        setEnviando(true);
        setError('');
        try {
            const nuevo = await api.post('/legal/aceptar', {
                documentos: documentos.map(d => ({ id: d.id, version: versiones[d.id] }))
            });
            setEstado(nuevo);
        } catch (e) {
            setError(e.message || 'No se pudo registrar la aceptación.');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm" style={{ margin: 0 }}>
            <div className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]" role="dialog" aria-modal="true" aria-labelledby="titulo-legal">
                <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50 shrink-0">
                    <h2 id="titulo-legal" className="text-lg font-black text-slate-900 tracking-tight">Antes de seguir, revisa y acepta estos documentos</h2>
                    <p className="text-xs font-semibold text-slate-500">
                        Los aceptas en nombre de la clínica. Quedará registrado quién los aceptó, cuándo y qué versión.
                    </p>
                </div>

                <div className="p-6 overflow-y-auto flex-1 space-y-6">
                    {documentos.map(d => (
                        <div key={d.id} className="space-y-3">
                            <h3 className="text-base font-black text-slate-900">{d.titulo}</h3>
                            <div className="max-h-72 overflow-y-auto p-4 rounded-2xl border border-slate-200 bg-slate-50">
                                <TextoLegal id={d.id} onCargado={(doc) => setVersiones(v => ({ ...v, [d.id]: doc.version }))} />
                            </div>
                            <label className="flex items-start gap-3 text-sm font-semibold text-slate-700">
                                <input type="checkbox" className="mt-1" checked={!!marcados[d.id]} disabled={!versiones[d.id]}
                                       onChange={(e) => setMarcados(m => ({ ...m, [d.id]: e.target.checked }))} />
                                He leído y acepto el documento «{d.titulo}».
                            </label>
                        </div>
                    ))}
                    {error && <p className="text-sm font-bold text-rose-700">{error}</p>}
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 px-6 py-4 border-t border-slate-100 shrink-0">
                    <button onClick={logout} className="px-4 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl">Cerrar sesión</button>
                    <button onClick={aceptar} disabled={!todosMarcados || enviando}
                            className="px-5 py-2.5 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl disabled:opacity-50">
                        {enviando ? 'Guardando…' : 'Aceptar y continuar'}
                    </button>
                </div>
            </div>
        </div>
    );
}
